import { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row, Tab, Table, Tabs } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { object } from "yup";
import PageTitle from "../../components/Elements/PageTitle/PageTitle.tsx";
import { useGet } from "../../components/Fetch/useFetch.tsx";
import MyFormik from "../../components/Form/MyFormik.tsx";
import { IconExport, IconList } from "../../components/Icon/Icon.tsx";
import MapRapportPersonnalise from "../../components/Map/MapRapportPersonnalise/MapRapportPersonnalise.tsx";
import PaginationFront from "../../components/PaginationFront/PaginationFront.tsx";
import url from "../../module/fetch.tsx";
import { useToastContext } from "../../module/Toast/ToastProvider.tsx";
import { requiredString } from "../../module/validators.tsx";
import {
  DynamicFormParametreFront,
  default as GenererForm,
} from "../../utils/buildDynamicForm.tsx";
import { downloadOutputFile } from "../../utils/fonctionsUtils.tsx";
import "./ExecuteRapportPersonnalise.css";
import { DEFAULT_PAGINATION } from "../../utils/constantsUtils.tsx";

const ExecuteRapportPersonnalise = () => {
  const { success: successToast, error: errorToast } = useToastContext();
  const location = useLocation();
  const locationState = location.state as {
    rapportPersonnaliseId?: string;
    peiNumeroComplet?: string;
  } | null;

  // Récupérer les paramètres GET depuis l'URL
  const searchParams = new URLSearchParams(location.search);
  const urlRapportId = searchParams.get("rapportPersonnaliseId");
  const urlPeiNumeroComplet = searchParams.get("peiNumeroComplet");

  // Les paramètres URL ont la priorité sur location.state
  const selectedRapportId =
    urlRapportId || locationState?.rapportPersonnaliseId;
  const peiNumeroComplet =
    urlPeiNumeroComplet || locationState?.peiNumeroComplet;

  const { data: listeRapportPerso } = useGet(
    url`/api/rapport-personnalise/list`,
  );

  const [tableau, setTableau] = useState<{
    headers: string[];
    values: any[];
    geometries: string[];
  }>();

  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(
    Number(localStorage.getItem("itemsPerPage")) || DEFAULT_PAGINATION,
  );
  const [activeTab, setActiveTab] = useState<string>("data");
  const [valuesFormik, setValuesFormik] = useState<any>();
  const [isDownload, setIsDownload] = useState(false);
  const [listeParametres, setListeParametres] = useState<
    DynamicFormParametreFront[]
  >([]);
  const [columnWidths, setColumnWidths] = useState<{ [key: number]: number }>(
    {},
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const formikRef = useRef<any>(null);
  const [hasAutoExecuted, setHasAutoExecuted] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const table = tableRef.current;

  // Hook pour gérer le redimensionnement des colonnes
  useEffect(() => {
    if (!tableau || !tableau.headers) {
      return;
    }
    let currentColumnIndex = -1;
    let startX = 0;
    let startWidth = 0;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      if (currentColumnIndex === -1) {
        return;
      }

      const newWidth = Math.max(50, startWidth + e.clientX - startX);
      setColumnWidths((prev) => ({ ...prev, [currentColumnIndex]: newWidth }));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("resizing");
      currentColumnIndex = -1;
    };

    const handleMouseDown = (e: MouseEvent, columnIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      const th = (e.target as HTMLElement).closest("th");
      if (!th) {
        return;
      }

      currentColumnIndex = columnIndex;
      startX = e.clientX;
      startWidth = th.offsetWidth;

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.classList.add("resizing");
    };

    if (table) {
      const resizers = table.querySelectorAll(".column-resizer");
      resizers.forEach((resizer, index) => {
        const handler = (e: Event) => handleMouseDown(e as MouseEvent, index);
        resizer.addEventListener("mousedown", handler);

        // Stocker le handler pour le nettoyage
        (resizer as any)._handler = handler;
      });
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("resizing");

      // Nettoyer les gestionnaires d'événements
      if (table) {
        const resizers = table.querySelectorAll(".column-resizer");
        resizers.forEach((resizer) => {
          const handler = (resizer as any)._handler;
          if (handler) {
            resizer.removeEventListener("mousedown", handler);
          }
        });
      }
    };
  }, [tableau, table]);

  // On exécute directement le rapport si on a tous les éléments nécessaires (cas de la navigation depuis la fiche résumé)
  useEffect(() => {
    if (
      !hasAutoExecuted &&
      selectedRapportId &&
      peiNumeroComplet &&
      formikRef.current &&
      listeParametres.length > 0
    ) {
      formikRef.current?.submitForm();
      setHasAutoExecuted(true);
    }
  }, [
    selectedRapportId,
    peiNumeroComplet,
    hasAutoExecuted,
    listeParametres.length,
  ]);

  useEffect(() => {
    setSelectedRowIndex(null);
  }, [offset, limit]);

  const getColumnStyle = (index: number) => {
    const width = columnWidths[index];
    if (width) {
      return { width: `${width}px`, maxWidth: `${width}px` };
    }

    return undefined;
  };

  const hasManualResize = Object.keys(columnWidths).length > 0;

  return (
    <Container fluid style={{ maxWidth: "95vw" }}>
      <PageTitle
        icon={<IconList />}
        title={"Exécuter des rapports personnalisés"}
      />
      <Row>
        <Col xs="auto" className="ms-auto">
          <Button
            onClick={() =>
              downloadOutputFile(
                "/api/rapport-personnalise/export-data",
                JSON.stringify({
                  rapportPersonnaliseId: valuesFormik?.rapportPersonnaliseId,
                  listeParametre: valuesFormik?.listeParametre,
                }),
                "rapport-personnalise.csv",
                "Export terminé",
                successToast,
                errorToast,
              )
            }
            disabled={valuesFormik == null || tableau == null}
          >
            Exporter les données <IconExport />
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            onClick={() => {
              setIsDownload(true);
              downloadOutputFile(
                "/api/rapport-personnalise/export-shp",
                JSON.stringify({
                  rapportPersonnaliseId: valuesFormik?.rapportPersonnaliseId,
                  listeParametre: valuesFormik?.listeParametre,
                }),
                "rapport-personnalise.zip",
                "",
                () => {
                  setIsDownload(false);
                  successToast("Export terminé");
                },
                () => {
                  setIsDownload(false);
                  return errorToast("Erreur lors de l'export");
                },
              );
            }}
            disabled={
              valuesFormik == null ||
              tableau == null ||
              tableau?.geometries == null ||
              tableau.geometries.length === 0 ||
              isDownload
            }
          >
            Exporter les données carto <IconExport />
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={3}>
          <MyFormik
            innerRef={formikRef}
            initialValues={getInitialValues(
              selectedRapportId,
              peiNumeroComplet,
            )}
            validationSchema={validationSchema}
            isPost={false}
            successToastMessage="La requête a bien été exécutée"
            submitUrl={`/api/rapport-personnalise/generer`}
            prepareVariables={(values) => {
              const value = prepareVariables(values, listeParametres);
              setValuesFormik(value);
              return value;
            }}
            onSubmit={(e) => {
              setTableau(e);
              setActiveTab("data");
              // Réinitialiser les largeurs de colonnes pour le nouveau tableau
              setColumnWidths({});
              setSelectedRowIndex(null);
              setOffset(0);
            }}
          >
            <GenererForm
              listeIdLibelleDescription={listeRapportPerso}
              contexteLibelle="Rapport personnalisé"
              url="/api/rapport-personnalise/parametres/"
              onParametresChange={setListeParametres}
              visibleMap={
                tableau?.geometries != null && tableau.geometries.length !== 0
              }
            />
          </MyFormik>
        </Col>
        <Col xs={12} lg={9}>
          <Tabs activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
            <Tab eventKey="data" title={"Données"}>
              {tableau === null ? (
                <Row className="m-3 text-center">
                  <Col className="text-center">Aucune donnée à afficher</Col>
                </Row>
              ) : (
                <div>
                  <div className="rapport-table-scroll">
                    <Table
                      bordered
                      striped
                      ref={tableRef}
                      className={`resizable-table ${hasManualResize ? "manual" : "auto"}`}
                    >
                      <thead>
                        <tr>
                          {tableau?.headers?.map((e, index) => (
                            <th
                              key={index}
                              style={getColumnStyle(index)}
                              title={e}
                            >
                              {e}
                              <div className="column-resizer" />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableau?.values
                          ?.slice(offset, offset + limit)
                          ?.map((ligne, index) => {
                            const rowIndex = offset + index;
                            return (
                              <tr
                                key={index}
                                className={`fw-normal ${selectedRowIndex === rowIndex ? "selected-row" : ""}`}
                                onClick={() => setSelectedRowIndex(rowIndex)}
                              >
                                {ligne.map((e: any, key: number) => (
                                  <td
                                    key={key}
                                    style={getColumnStyle(key)}
                                    title={e?.toString()}
                                  >
                                    {e?.toString()}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                  {tableau?.values && (
                    <PaginationFront
                      key={tableau?.values?.length}
                      values={tableau?.values}
                      setOffset={setOffset}
                      setLimit={setLimit}
                    />
                  )}
                </div>
              )}
            </Tab>
            <Tab
              eventKey="map"
              title="Carte"
              disabled={
                tableau?.geometries == null || tableau.geometries.length === 0
              }
            >
              <MapRapportPersonnalise wkt={tableau?.geometries || []} />
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export const getInitialValues = (
  selectedRapportId?: string,
  peiNumeroComplet?: string,
) => ({
  dynamicFormId: selectedRapportId || null,
  PEI_NUMERO_COMPLET: peiNumeroComplet || null,
});

export const validationSchema = object({
  dynamicFormId: requiredString,
});

export const prepareVariables = (
  values: any,
  listeParametres: DynamicFormParametreFront[],
) => {
  // Créer la liste complète des paramètres avec toutes les valeurs (valeur par défaut si non spécifiées)
  const listeParametre = listeParametres.map((param) => {
    const code = param.dynamicFormParametreCode;
    // Récupérer la valeur depuis les values, ou la valeur par défaut si non définie/vide
    const value =
      values[code] !== undefined && values[code] !== "" && values[code] !== null
        ? values[code]
        : (param.dynamicFormParametreValeurDefaut ?? null);

    return {
      rapportPersonnaliseParametreCode: code,
      value: value,
    };
  });

  return {
    rapportPersonnaliseId: values.dynamicFormId,
    listeParametre: listeParametre,
  };
};

export default ExecuteRapportPersonnalise;
